import { useEffect } from 'react'
import { Outlet, Navigate } from 'react-router-dom'
import { useAppContext } from '@/hooks/routing'
import { Loader2 } from 'lucide-react'
import { useTenant } from '@/contexts'

/**
 * Layout wrapper for location-scoped routes (/:locationSlug/*).
 *
 * Responsibilities:
 * 1. Validates that the locationSlug param corresponds to a real location
 * 2. Syncs TenantContext.currentLocation with the URL-resolved location
 * 3. Redirects to /dashboard if location is invalid
 * 4. Provides location context to child routes via useAppContext hook
 *
 * This component does NOT render its own layout (sidebar, header, etc.)
 * because those are handled by AppLayout which wraps both org-wide and
 * location-scoped routes.
 */
export function LocationLayout() {
  const { isValidLocation, locationId, locations } = useAppContext()
  const { isLoading, locations: tenantLocations, setCurrentLocation } = useTenant()

  // Sync TenantContext.currentLocation with URL-resolved location
  // This ensures components that read useTenant().currentLocation (e.g. HangarDialog)
  // always see the location matching the URL — no split-brain between URL and context
  useEffect(() => {
    if (isValidLocation && locationId) {
      const match = tenantLocations.find((loc) => loc.id === locationId)
      if (match) {
        setCurrentLocation(match)
      }
    }
  }, [isValidLocation, locationId, tenantLocations, setCurrentLocation])

  // Show loading while tenant data is being fetched
  // Also treat empty locations array as loading (race condition safeguard)
  if (isLoading || locations.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Invalid location slug - redirect to org dashboard
  // Only redirect after locations are loaded (locations.length > 0)
  if (!isValidLocation) {
    return <Navigate to="/dashboard" replace />
  }

  // Valid location - render the child routes
  // The useAppContext hook will provide the location context to all children
  return <Outlet />
}

/**
 * Context provider specifically for location-scoped pages.
 * This is a lighter alternative if you just need to validate location exists
 * without the full routing context.
 */
export function useLocationContext() {
  const { locationSlug, locationId, locationName, isValidLocation } = useAppContext()

  if (!isValidLocation) {
    throw new Error('useLocationContext must be used within a valid location route')
  }

  return {
    locationSlug: locationSlug!,
    locationId: locationId!,
    locationName: locationName!,
  }
}
